import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../theme/context/ThemeContext';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import nvgtrLogo_Black from '../../../assets/nvgtrLogo_Black.svg';
import nvgtrLogo_White from '../../../assets/nvgtrLogo_White.svg';
import { useSystemStatus } from '../../../hooks/useSystemStatus';
import { getBrandAssetsForDomain } from '../../../utils/domainAssetMap';
import { getPublicStatusHomeUrl } from '../../../utils/publicStatusPage';
import { hasActiveSubscription } from '../../../utils/planPermissions';

// Initialize Stripe - use test keys in development, live keys in production
const getStripePublishableKey = () => {
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development' || 
                       window.location.hostname === 'localhost' ||
                       window.location.hostname.includes('192.168');
  
  const key = isDevelopment 
    ? process.env.REACT_APP_STRIPE_PUBLIC_KEY_TEST 
    : process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    
  console.log('🔑 Frontend Stripe Configuration:', {
    nodeEnv: process.env.NODE_ENV,
    hostname: window.location.hostname,
    isDevelopment,
    usingTestKey: isDevelopment,
    keyPrefix: key ? key.substring(0, 12) + '...' : 'NOT SET'
  });
  
  return key;
};

const stripePromise = loadStripe(getStripePublishableKey());

// Price IDs for each plan - TEST for development, LIVE for production
const PLAN_PRICE_IDS = (() => {
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development' || 
                       window.location.hostname === 'localhost' ||
                       window.location.hostname.includes('192.168');
  
  return isDevelopment ? {
    // TEST PRICE IDs - Monthly
    solo_monthly: 'price_1RLr7DEKaOZiHmDtGIbNHla8', // TEST $20/mo Solo Plan
    pro_monthly: 'price_1RLr8UEKaOZiHmDtHcScJJV8',  // TEST $50/mo Pro Plan  
    max_monthly: 'price_1RLr8tEKaOZiHmDtYfPGZBYU',  // TEST $250/mo Max Plan
    // TEST PRICE IDs - Annual
    solo_annual: 'price_1RdwK0E6HGxDj1tZYDw30Ws6',   // TEST Annual Solo Plan
    pro_annual: 'price_1RdwKwE6HGxDj1tZ22cpf6Si',   // TEST Annual Pro Plan
    max_annual: 'price_1RdwLfE6HGxDj1tZk9NH9PYN',   // TEST Annual Max Plan
    // TEST Additional Member Prices
    pro_member_monthly: 'price_1RdwIkE6HGxDj1tZsoTGMHZe', // TEST $25/mo per additional Pro member
    pro_member_annual: 'price_1RdwIkE6HGxDj1tZs9J7mjKk',   // TEST Annual per additional Pro member
    max_member_monthly: 'price_1RdwMTE6HGxDj1tZquREIkEF', // TEST $35/mo per additional Max member
    max_member_annual: 'price_1RdwNQE6HGxDj1tZkTCRoUNc'    // TEST Annual per additional Max member
  } : {
    // LIVE PRICE IDs - Monthly
    solo_monthly: 'price_1RVlxVE6HGxDj1tZe6cw3ZEr', // LIVE $20/mo Solo Plan
    pro_monthly: 'price_1RVlxUE6HGxDj1tZSqk5hrNd',  // LIVE $99/mo Pro Plan  
    max_monthly: 'price_1RVlxRE6HGxDj1tZiMBx3WxN',  // LIVE $349/mo Max Plan
    // LIVE PRICE IDs - Annual (17% discount)
    solo_annual: 'price_LIVE_SOLO_ANNUAL',           // LIVE $16.60/mo ($199/year) Solo Plan
    pro_annual: 'price_LIVE_PRO_ANNUAL',             // LIVE $82.17/mo ($986/year) Pro Plan
    max_annual: 'price_LIVE_MAX_ANNUAL',             // LIVE $289.67/mo ($3476/year) Max Plan
    // LIVE Additional Member Prices
    pro_member_monthly: 'price_LIVE_PRO_MEMBER_MONTHLY', // LIVE $25/mo per additional Pro member
    pro_member_annual: 'price_LIVE_PRO_MEMBER_ANNUAL',   // LIVE $20.75/mo ($249/year) per additional Pro member
    max_member_monthly: 'price_LIVE_MAX_MEMBER_MONTHLY', // LIVE $35/mo per additional Max member
    max_member_annual: 'price_LIVE_MAX_MEMBER_ANNUAL'    // LIVE $29.05/mo ($348.60/year) per additional Max member
  };
})();

function PaymentPage({ user, setUser, refreshUser }) {
  const location = useLocation();
  const { darkMode } = useTheme();
  const { systemStatus, isLoading: statusLoading } = useSystemStatus();
  const firstName = user?.firstName || 'there';
  
  // Force refresh user data when component mounts to check organization status
  useEffect(() => {
    if (refreshUser) {
      console.log('PaymentPage: Refreshing user data to check organization status...');
      refreshUser();
    }
  }, []); // Only run once on mount
  
  // Check if we're in the onboarding flow (pathname includes /auth/signup/payment)
  const isInOnboardingFlow = location.pathname.includes('/auth/signup/payment');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAnnual, setIsAnnual] = useState(true); // Start with annual selected
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamSeats, setTeamSeats] = useState(1);
  const [modalPlan, setModalPlan] = useState(null);
  const [isEditingSeats, setIsEditingSeats] = useState(false);
  const [editingSeatsValue, setEditingSeatsValue] = useState('1');
  const fontFamilyStyle = { fontFamily: 'var(--chat-font-family)' };
  
  // Comprehensive feature list - all features that exist across all plans
  // For features with variants (users, context, requests), we show the appropriate one per plan
  const getFeaturesForPlan = (plan) => {
    const baseFeatures = [
      // User/Team - show appropriate variant
      plan === 'solo' ? { id: 'users', label: '1 User', included: true } :
      plan === 'pro' ? { id: 'team10', label: 'Up to 10 Team Members', included: true } :
      { id: 'team50', label: 'Up to 50 Team Members', included: true },
      
      // Context - show appropriate variant
      plan === 'solo' ? { id: 'context1gb', label: '1 GB Context', included: true } :
      plan === 'pro' ? { id: 'context5gb', label: '5 GB Context', included: true } :
      { id: 'context25gb', label: '25 GB Context', included: true },
      
      // Requests - show appropriate variant
      plan === 'solo' ? { id: 'requests500', label: '500 Fast Requests/mo', included: true } :
      { id: 'requests500user', label: '500 Requests/user/mo', included: true },
      
      // Common features
      { id: 'aiModels', label: 'AI Models Access', included: true },
      { id: 'customDomain', label: 'Custom Domain', included: plan !== 'solo' },
      { id: 'prioritySupport', label: 'Priority Support', included: plan !== 'solo' },
      { id: 'superbucket', label: 'SuperBucket™ Access', included: plan === 'max' },
      { id: 'enterprise', label: 'Enterprise Features', included: plan === 'max' },
    ];
    
    return baseFeatures;
  };
  
  // Helper to check if a feature is included in a plan
  const isFeatureIncluded = (feature) => {
    return feature.included === true;
  };
  
  // Feature item component
  const FeatureItem = ({ feature }) => {
    const included = isFeatureIncluded(feature);
    return (
      <div className="flex items-center" style={{ opacity: included ? 1 : 1 }}>
        <div 
          className="flex items-center justify-center mr-2 flex-shrink-0"
          style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            border: included 
              ? `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'}`
              : `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.4)'}`,
            backgroundColor: included 
              ? (darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)')
              : 'transparent',
          }}
        >
          {included && (
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" style={{ opacity: 1 }}>
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <span 
          className="text-sm"
          style={{
            color: included 
              ? (darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)')
              : (darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.5)'),
          }}
        >
          {feature.label}
        </span>
      </div>
    );
  };
  
  // Logo handling (similar to Onboarding component)
  const isRootDomain = useCallback(() => {
    const hostname = window.location.hostname;
    return hostname.includes('heymidus.com') || hostname.includes('midus.co');
  }, []);
  
  const getCachedLogo = useCallback(() => {
    if (isRootDomain()) {
      return darkMode ? nvgtrLogo_White : nvgtrLogo_Black;
    }
    try {
      const cachedLogoData = localStorage.getItem('logoCache');
      if (cachedLogoData) {
        const { logoUrl, isDark } = JSON.parse(cachedLogoData);
        if (isDark === darkMode && logoUrl) {
          if (logoUrl.includes('nvgtr_BM')) {
            return darkMode ? nvgtrLogo_White : nvgtrLogo_Black;
          }
          return logoUrl;
        }
      }
    } catch (e) {
      // If any error occurs reading cache, use default
    }
    return darkMode ? nvgtrLogo_White : nvgtrLogo_Black;
  }, [darkMode, isRootDomain]);
  
  const [logoUrl, setLogoUrl] = useState(getCachedLogo());
  const [isLogoLoading, setIsLogoLoading] = useState(() => {
    if (isRootDomain()) {
      return false;
    }
    try {
      const cachedLogoData = localStorage.getItem('logoCache');
      if (cachedLogoData) {
        const { isDark, logoUrl } = JSON.parse(cachedLogoData);
        if (isDark === darkMode && logoUrl) {
          return false;
        }
      }
    } catch (e) {
      // If error reading cache, show loading
    }
    return true;
  });
  
  const updateLogoUrl = useCallback((url) => {
    if (isRootDomain()) {
      const defaultUrl = darkMode ? nvgtrLogo_White : nvgtrLogo_Black;
      setLogoUrl(defaultUrl);
      setIsLogoLoading(false);
      return;
    }
    if (url && typeof url === 'string' && url.includes('nvgtr_BM')) {
      url = darkMode ? nvgtrLogo_White : nvgtrLogo_Black;
    }
    setLogoUrl(url);
    setIsLogoLoading(false);
    try {
      localStorage.setItem('logoCache', JSON.stringify({
        logoUrl: url,
        isDark: darkMode,
        timestamp: Date.now()
      }));
    } catch (e) {
      // Ignore storage errors
    }
  }, [darkMode, isRootDomain]);
  
  // Logo spinner component
  const LogoSpinner = ({ darkMode }) => {
    return (
      <div className="h-8 w-auto flex items-center justify-center">
        <div 
          className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: darkMode ? '#444444' : '#c3c3c3',
            borderTopColor: 'transparent'
          }}
        />
      </div>
    );
  };
  
  // Load logo on mount
  useEffect(() => {
    if (!isRootDomain()) {
      const loadLogo = async () => {
        try {
          const domainData = await getBrandAssetsForDomain();
          if (domainData?.logoUrl) {
            updateLogoUrl(domainData.logoUrl);
          } else {
            updateLogoUrl(darkMode ? nvgtrLogo_White : nvgtrLogo_Black);
          }
        } catch (error) {
          console.error('Error loading logo:', error);
          updateLogoUrl(darkMode ? nvgtrLogo_White : nvgtrLogo_Black);
        }
      };
      loadLogo();
    }
  }, [darkMode, isRootDomain, updateLogoUrl]);
  
  // Refs for measuring button widths for sliding indicator
  const annualButtonRef = useRef(null);
  const monthlyButtonRef = useRef(null);
  const [annualWidth, setAnnualWidth] = useState(0);
  const [monthlyWidth, setMonthlyWidth] = useState(0);
  
  // Measure button widths
  useEffect(() => {
    const measureWidths = () => {
      if (annualButtonRef.current) {
        setAnnualWidth(annualButtonRef.current.offsetWidth);
      }
      if (monthlyButtonRef.current) {
        setMonthlyWidth(monthlyButtonRef.current.offsetWidth);
      }
    };
    
    measureWidths();
    window.addEventListener('resize', measureWidths);
    return () => window.removeEventListener('resize', measureWidths);
  }, [isAnnual]);

  // Pricing calculations
  const getDisplayPrice = (monthlyPrice) => {
    if (isAnnual) {
      return Math.round(monthlyPrice * 0.83); // 17% discount for annual
    }
    return monthlyPrice;
  };

  // Check if user should be redirected based on their current state
  useEffect(() => {
    // If user is a member (not an owner) and already has company subscription, redirect to home
    const userRole = localStorage.getItem('userRole') || '';
    if (hasActiveSubscription(user) && userRole === 'member') {
      console.log("Member user already has subscription through organization, redirecting to home");
      window.location.href = '/';
      return;
    }
  }, [user]);

  const handleContinue = async (planOverride = null, seats = 1) => {
    const planToUse = planOverride || selectedPlan;
    if (!planToUse) {
      alert("Please select a plan to continue.");
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Determine billing period and construct price key
      const billingPeriod = isAnnual ? 'annual' : 'monthly';
      const basePriceKey = `${planToUse}_${billingPeriod}`;
      
      // Get the base price ID for the selected plan and billing period
      const basePriceId = PLAN_PRICE_IDS[basePriceKey];
      
      if (!basePriceId) {
        console.error('Invalid plan/billing combination:', basePriceKey);
        alert("Error: Invalid plan selected. Please try again or contact support.");
        setIsProcessing(false);
        return;
      }

      // Prepare line items for Stripe checkout
      const lineItems = [
        {
          price: basePriceId,
          quantity: 1
        }
      ];

      // Add additional team member line items for Pro/Max plans
      if ((planToUse === 'pro' || planToUse === 'max') && seats > 1) {
        const memberPriceKey = `${planToUse}_member_${billingPeriod}`;
        const memberPriceId = PLAN_PRICE_IDS[memberPriceKey];
        
        if (memberPriceId) {
          lineItems.push({
            price: memberPriceId,
            quantity: seats - 1 // Additional members (excluding base user)
          });
        } else {
          console.error('Invalid member price key:', memberPriceKey);
          alert("Error: Unable to configure team pricing. Please try again or contact support.");
          setIsProcessing(false);
          return;
        }
      }

      // Get the actual user ID and company ID from localStorage or the user prop
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const authState = JSON.parse(localStorage.getItem('authState') || '{}');
      const actualUserId = user._id || user.id || storedUser.id || storedUser._id;
      const actualCompanyId = user.companyId || storedUser.companyId || storedUser.organizationId;
      
      // Prepare metadata with multiple fallbacks for organization ID
      const metadata = {
        plan: planToUse,
        userId: actualUserId,
        billingPeriod: billingPeriod,
        teamSeats: seats.toString()
      };

      // Add signup data to metadata so webhook can save it if needed
      if (user.firstName) metadata.firstName = user.firstName;
      if (user.lastName) metadata.lastName = user.lastName;
      if (user.email) metadata.email = user.email;
      if (user.companyName) metadata.companyName = user.companyName;
      if (user.location) metadata.location = user.location;
      // Get phoneNumber from authState or storedUser
      const phoneNumberForMetadata = authState.phoneNumber || user.phoneNumber || storedUser.phoneNumber;
      if (phoneNumberForMetadata) metadata.phoneNumber = phoneNumberForMetadata;

      // Add organization ID with multiple fallbacks
      if (actualCompanyId) {
        metadata.organizationId = actualCompanyId;
        metadata.companyId = actualCompanyId; // Also send as companyId for backward compatibility
      } else if (user.organizationId || storedUser.organizationId) {
        const orgId = user.organizationId || storedUser.organizationId;
        metadata.organizationId = orgId;
        metadata.companyId = orgId;
      } else if ((user.company && user.company.id) || (storedUser.company && storedUser.company.id)) {
        const companyId = (user.company && user.company.id) || (storedUser.company && storedUser.company.id);
        metadata.organizationId = companyId;
        metadata.companyId = companyId;
      }
      
      console.log('🛒 Creating Stripe checkout session:', {
        plan: planToUse, 
        billingPeriod: billingPeriod,
        teamSeats: seats,
        lineItems: lineItems,
        userId: actualUserId,
        userEmail: user.email,
        metadata: metadata,
        userObject: {
          companyId: actualCompanyId,
          organizationId: user.organizationId || storedUser.organizationId,
          company: user.company || storedUser.company
        },
        phoneNumber: phoneNumberForMetadata
      });
      
      // Create Stripe checkout session
      const checkoutResponse = await axios.post(`${process.env.REACT_APP_API_URL}/payment/create-checkout-session`, {
        productId: planToUse,
        lineItems: lineItems,
        metadata: metadata
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Checkout session created:', checkoutResponse.data);
      
      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId: checkoutResponse.data.sessionId
      });

      if (error) {
        console.error('Stripe checkout error:', error);
        alert("Error redirecting to payment. Please try again or contact support.");
        setIsProcessing(false);
      }
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert(`Payment setup failed: ${errorMessage}. Please try again or contact support.`);
      setIsProcessing(false);
    }
  };

  return (
    <>
    <div data-auth-container className={`flex flex-col items-center justify-start ${isInOnboardingFlow ? '' : 'bg-white dark:bg-[#030303]'} relative overflow-x-hidden`} style={{ minHeight: isInOnboardingFlow ? 'auto' : '100vh', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* Gradient overlay - hide when in onboarding flow */}
      {!isInOnboardingFlow && (
      <div 
        className="absolute left-0 right-0 bottom-0 pointer-events-none"
        style={{
          height: '40vh',
          background: darkMode 
            ? 'radial-gradient(ellipse 120% 100% at center bottom, rgba(255, 189, 163, 0.15) 0%, rgba(189, 204, 255, 0.08) 50%, rgba(189, 204, 255, 0.04) 70%, rgba(189, 204, 255, 0.02) 85%, rgba(0, 0, 0, 0) 100%)'
            : 'radial-gradient(ellipse 120% 100% at center bottom, rgba(255, 189, 163, 0.6) 0%, rgba(189, 204, 255, 0.4) 50%, rgba(189, 204, 255, 0.25) 70%, rgba(189, 204, 255, 0.15) 85%, rgba(255, 255, 255, 0) 100%)'
        }}
      />
      )}
      
      {/* Logo at top - hide when in onboarding flow */}
      {!isInOnboardingFlow && (
      <div className="absolute top-[35px] md:top-[45px] left-1/2 transform -translate-x-1/2 px-4 md:px-0">
        {isLogoLoading ? (
          <LogoSpinner darkMode={darkMode} />
        ) : (
          <img 
            src={logoUrl}
            alt="Logo" 
            className="h-6 md:h-8"
            onError={() => {
              updateLogoUrl(darkMode ? nvgtrLogo_White : nvgtrLogo_Black);
            }}
            onLoad={() => {
              setIsLogoLoading(false);
            }}
          />
        )}
      </div>
      )}
      
      {/* Main content */}
      <div className={`w-full p-8 ${isInOnboardingFlow ? 'mt-0 mb-0' : 'mt-[80px] md:mt-[100px] mb-[180px] md:mb-[200px]'} flex-1`}>
        <div className="flex flex-col items-center w-full mx-auto" style={fontFamilyStyle}>
          {/* Heading and description container */}
          <div className="flex flex-col items-center gap-[8px] w-full mb-[32px]">
            <h2 className="text-[#000000] dark:text-white text-[24px] md:text-[32px]" style={{ fontFamily: 'PP Editorial New, serif', fontWeight: 200, fontStyle: 'italic' }}>Choose your plan, {firstName}!</h2>
            <p className="text-[#6B6B6B] dark:text-[#888888] text-[15px] font-normal">
              Your account is ready. Select the plan that best fits your needs.
            </p>
          </div>
          
          {/* Annual/Monthly Toggle */}
      <div 
        className="flex items-center justify-center mx-auto relative"
        style={{
          boxSizing: 'border-box',
          width: 'min-content',
          height: 'min-content',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: '4px 8px',
          boxShadow: darkMode 
            ? 'inset 0px 0px 24px 0px rgba(255, 255, 255, 0.12)' 
            : 'inset 0px 0px 24px 0px rgba(0, 0, 0, 0.12)',
          backgroundColor: darkMode 
            ? 'rgba(255, 255, 255, 0.08)' 
            : 'rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          gap: '12px',
          borderRadius: '24px',
          border: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        {/* Sliding active indicator */}
        <div
          style={{
            boxSizing: 'border-box',
            height: '24px',
            backgroundColor: darkMode ? '#575757' : '#D0D0D0',
            opacity: darkMode ? 0.4 : 0.6,
            borderRadius: '24px',
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            transition: 'left 0.3s ease, width 0.3s ease',
            zIndex: 1,
            padding: '4px 16px',
            left: isAnnual ? '8px' : annualWidth ? `calc(8px + ${annualWidth}px + 12px)` : 'auto',
            width: isAnnual ? (annualWidth ? `${annualWidth}px` : 'auto') : (monthlyWidth ? `${monthlyWidth}px` : 'auto'),
          }}
        />
        
        {/* Annual container */}
        <button
          ref={annualButtonRef}
          onClick={() => setIsAnnual(true)}
          className="relative z-10 flex items-center gap-2"
          style={{
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4px 16px',
            backgroundColor: 'transparent',
            borderRadius: '24px',
            border: 'none',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <span className={`text-sm font-medium transition-all ${
            isAnnual 
              ? (darkMode ? 'text-white' : 'text-black') 
              : (darkMode ? 'text-[#888888]' : 'text-[#6B6B6B]')
          }`}>
            Annual
          </span>
          <span className={`text-xs whitespace-nowrap ${darkMode ? 'text-[#888888]' : 'text-[#6B6B6B]'}`}>Save 17%</span>
        </button>
        
        {/* Monthly container */}
        <button
          ref={monthlyButtonRef}
          onClick={() => setIsAnnual(false)}
          className="relative z-10"
          style={{
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4px 16px',
            backgroundColor: 'transparent',
            borderRadius: '24px',
            border: 'none',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <span className={`text-sm font-medium transition-all ${
            !isAnnual 
              ? (darkMode ? 'text-white' : 'text-black') 
              : (darkMode ? 'text-[#888888]' : 'text-[#6B6B6B]')
          }`}>
            Monthly
          </span>
        </button>
      </div>
      
          {/* Pricing cards container */}
          <div className="mt-[40px]">
          <div className="flex flex-col md:flex-row gap-4 md:gap-4 w-full justify-center items-stretch md:items-start">
        <div 
          className="relative flex flex-col items-start justify-start text-black dark:text-white transition-all duration-200 w-full md:w-[320px] md:flex-shrink-0"
          style={{
            boxSizing: 'border-box',
            height: 'min-content',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            padding: '32px',
            boxShadow: darkMode 
              ? 'inset 0px 0px 24px 1px rgba(255, 255, 255, 0.08)' 
              : 'inset 0px 0px 24px 1px rgba(0, 0, 0, 0.08)',
            backgroundColor: darkMode 
              ? 'rgba(255, 255, 255, 0.04)' 
              : 'rgba(0, 0, 0, 0.04)',
            overflow: 'visible',
            gap: '16px',
            borderRadius: '22px',
            border: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.12)' 
              : '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <div className="text-left w-full">
            <div style={{ marginBottom: '16px' }}>
              <h3 className="text-lg font-medium mb-1">Solo</h3>
              {/* Separator line */}
              <div 
                style={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  marginBottom: '16px',
                }}
              />
              <div className="flex items-baseline">
                <span className="text-4xl" style={{ fontFamily: 'PP Editorial New, serif', fontStyle: 'italic' }}>${getDisplayPrice(20)}</span>
                <span className="text-sm opacity-70 ml-1">/month</span>
              </div>
            </div>
            
            <button 
              onClick={async () => {
                setSelectedPlan('solo');
                await handleContinue('solo');
              }}
              disabled={isProcessing}
              className={`font-normal text-sm transition-all hover:opacity-80 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{
                boxSizing: 'border-box',
                width: '100%',
                height: 'min-content',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor: darkMode ? '#ffffff' : '#000000',
                color: darkMode ? '#000000' : '#ffffff',
                overflow: 'visible',
                gap: '8px',
                borderRadius: '25px',
                marginBottom: '16px',
                border: 'none',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
              }}
            >
              {isProcessing && selectedPlan === 'solo' ? 'Processing...' : 'Continue with Solo'}
            </button>
            
            {/* Separator line */}
            <div 
              style={{
                width: '100%',
                height: '1px',
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                marginBottom: '16px',
              }}
            />
            
            <div className="text-sm" style={{ gap: '16px', display: 'flex', flexDirection: 'column' }}>
              {getFeaturesForPlan('solo').map((feature) => (
                <FeatureItem key={feature.id} feature={feature} />
              ))}
            </div>
          </div>
        </div>
        
        <div 
          className="relative flex flex-col items-start justify-start text-black dark:text-white transition-all duration-200 w-full md:w-[320px] md:flex-shrink-0"
          style={{
            boxSizing: 'border-box',
            height: 'min-content',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            padding: '32px',
            boxShadow: darkMode 
              ? 'inset 0px 0px 24px 1px rgba(255, 255, 255, 0.08)' 
              : 'inset 0px 0px 24px 1px rgba(0, 0, 0, 0.08)',
            backgroundColor: darkMode 
              ? 'rgba(255, 255, 255, 0.04)' 
              : 'rgba(0, 0, 0, 0.04)',
            overflow: 'visible',
            gap: '16px',
            borderRadius: '22px',
            border: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.12)' 
              : '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span 
              className="text-xs font-medium px-3 py-1 rounded-full"
              style={{
                backgroundColor: darkMode ? 'rgba(176, 135, 123, 0.2)' : 'rgba(176, 135, 123, 0.15)',
                color: darkMode ? '#B0877B' : '#8B6B5A',
                border: darkMode ? '1px solid rgba(176, 135, 123, 0.3)' : '1px solid rgba(176, 135, 123, 0.25)',
                backdropFilter: 'blur(10px)',
              }}
            >
              Most Popular
            </span>
          </div>
          
          <div className="text-left w-full">
            <div style={{ marginBottom: '16px' }}>
              <h3 className="text-lg font-medium mb-1">Pro</h3>
              {/* Separator line */}
              <div 
                style={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  marginBottom: '16px',
                }}
              />
              <div className="flex items-baseline">
                <span className="text-4xl" style={{ fontFamily: 'PP Editorial New, serif', fontStyle: 'italic' }}>${getDisplayPrice(99)}</span>
                <span className="text-sm opacity-70 ml-1">/month</span>
              </div>
            </div>
            
            <button 
              onClick={() => {
                setModalPlan('pro');
                setShowTeamModal(true);
              }}
              disabled={isProcessing}
              className={`font-normal text-sm transition-all hover:opacity-80 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{
                boxSizing: 'border-box',
                width: '100%',
                height: 'min-content',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor: darkMode ? '#ffffff' : '#000000',
                color: darkMode ? '#000000' : '#ffffff',
                overflow: 'visible',
                gap: '8px',
                borderRadius: '25px',
                marginBottom: '16px',
                border: 'none',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
              }}
            >
              Continue with Pro
            </button>
            
            {/* Separator line */}
            <div 
              style={{
                width: '100%',
                height: '1px',
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                marginBottom: '16px',
              }}
            />
            
            <div className="text-sm" style={{ gap: '16px', display: 'flex', flexDirection: 'column' }}>
              {getFeaturesForPlan('pro').map((feature) => (
                <FeatureItem key={feature.id} feature={feature} />
              ))}
            </div>
          </div>
        </div>
        
        <div 
          className="relative flex flex-col items-start justify-start text-black dark:text-white transition-all duration-200 w-full md:w-[320px] md:flex-shrink-0"
          style={{
            boxSizing: 'border-box',
            height: 'min-content',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            padding: '32px',
            boxShadow: darkMode 
              ? 'inset 0px 0px 24px 1px rgba(255, 255, 255, 0.08)' 
              : 'inset 0px 0px 24px 1px rgba(0, 0, 0, 0.08)',
            backgroundColor: darkMode 
              ? 'rgba(255, 255, 255, 0.04)' 
              : 'rgba(0, 0, 0, 0.04)',
            overflow: 'visible',
            gap: '16px',
            borderRadius: '22px',
            border: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.12)' 
              : '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <div className="text-left w-full">
            <div style={{ marginBottom: '16px' }}>
              <h3 className="text-lg font-medium mb-1">Max</h3>
              {/* Separator line */}
              <div 
                style={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  marginBottom: '16px',
                }}
              />
              <div className="flex items-baseline">
                <span className="text-4xl" style={{ fontFamily: 'PP Editorial New, serif', fontStyle: 'italic' }}>${getDisplayPrice(359)}</span>
                <span className="text-sm opacity-70 ml-1">/month</span>
              </div>
            </div>
            
            <button 
              onClick={() => {
                setModalPlan('max');
                setShowTeamModal(true);
              }}
              disabled={isProcessing}
              className={`font-normal text-sm transition-all hover:opacity-80 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{
                boxSizing: 'border-box',
                width: '100%',
                height: 'min-content',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor: darkMode ? '#ffffff' : '#000000',
                color: darkMode ? '#000000' : '#ffffff',
                overflow: 'visible',
                gap: '8px',
                borderRadius: '25px',
                marginBottom: '16px',
                border: 'none',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
              }}
            >
              Continue with Max
            </button>
            
            {/* Separator line */}
            <div 
              style={{
                width: '100%',
                height: '1px',
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                marginBottom: '16px',
              }}
            />
            
            <div className="text-sm" style={{ gap: '16px', display: 'flex', flexDirection: 'column' }}>
              {getFeaturesForPlan('max').map((feature) => (
                <FeatureItem key={feature.id} feature={feature} />
              ))}
            </div>
          </div>
        </div>
          </div>
          </div>
        </div>
      </div>

      {/* Team Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-50" style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.85)' }}>
          <div 
            className="rounded-2xl p-6 w-full max-w-md mx-4 text-black dark:text-white"
            style={{
              boxSizing: 'border-box',
              boxShadow: darkMode 
                ? 'inset 0px 0px 24px 1px rgba(255, 255, 255, 0.08)' 
                : 'inset 0px 0px 24px 1px rgba(0, 0, 0, 0.08)',
              backgroundColor: darkMode 
                ? 'rgba(255, 255, 255, 0.04)' 
                : 'rgba(0, 0, 0, 0.02)',
              border: darkMode 
                ? '1px solid rgba(255, 255, 255, 0.12)' 
                : '1px solid rgba(0, 0, 0, 0.12)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-black dark:text-white">
                {modalPlan === 'pro' ? 'Pro' : 'Max'} 
                <span 
                  className="text-xs font-medium px-2 py-1 rounded-full ml-2"
                  style={{
                    backgroundColor: darkMode ? 'rgba(176, 135, 123, 0.2)' : 'rgba(176, 135, 123, 0.15)',
                    color: darkMode ? '#B0877B' : '#8B6B5A',
                    border: darkMode ? '1px solid rgba(176, 135, 123, 0.3)' : '1px solid rgba(176, 135, 123, 0.25)',
                  }}
                >
                  Beta
                </span>
              </h3>
              <button 
                onClick={() => setShowTeamModal(false)}
                className={`${darkMode ? 'text-[#888888] hover:text-white' : 'text-[#6B6B6B] hover:text-black'} transition-colors`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>


            {/* Team Seats */}
            <div className="mb-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-black dark:text-white">Team seats</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const maxSeats = modalPlan === 'pro' ? 10 : 50;
                      setTeamSeats(Math.max(1, Math.min(teamSeats - 1, maxSeats)));
                      setIsEditingSeats(false);
                    }}
                    className="w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                    style={{
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                      color: darkMode ? '#888888' : '#6B6B6B',
                      border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{ fontSize: '16px', lineHeight: '1', display: 'block', transform: 'translateY(-1px)' }}>−</span>
                  </button>
                  {isEditingSeats ? (
                    <input
                      type="number"
                      value={editingSeatsValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          setEditingSeatsValue(value);
                        }
                      }}
                      onBlur={() => {
                        const numValue = parseInt(editingSeatsValue) || 1;
                        const maxSeats = modalPlan === 'pro' ? 10 : 50;
                        const finalValue = Math.max(1, Math.min(numValue, maxSeats));
                        setTeamSeats(finalValue);
                        setEditingSeatsValue(finalValue.toString());
                        setIsEditingSeats(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        } else if (e.key === 'Escape') {
                          setEditingSeatsValue(teamSeats.toString());
                          setIsEditingSeats(false);
                        }
                      }}
                      autoFocus
                      className="text-base font-medium text-black dark:text-white bg-transparent border-none outline-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      style={{ 
                        minWidth: '24px', 
                        width: '40px'
                      }}
                      onWheel={(e) => e.target.blur()}
                    />
                  ) : (
                    <span 
                      className="text-base font-medium text-black dark:text-white cursor-pointer" 
                      style={{ minWidth: '24px', textAlign: 'center' }}
                      onClick={() => {
                        setEditingSeatsValue(teamSeats.toString());
                        setIsEditingSeats(true);
                      }}
                    >
                      {teamSeats}
                    </span>
                  )}
                  <button
                    onClick={() => {
                      const maxSeats = modalPlan === 'pro' ? 10 : 50;
                      setTeamSeats(Math.max(1, Math.min(teamSeats + 1, maxSeats)));
                      setIsEditingSeats(false);
                    }}
                    className="w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                    style={{
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                      color: darkMode ? '#888888' : '#6B6B6B',
                      border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{ fontSize: '16px', lineHeight: '1', display: 'block', transform: 'translateY(-1px)' }}>+</span>
                  </button>
                </div>
              </div>
              <div className="text-xs text-[#999999] dark:text-[#666666]">${modalPlan === 'pro' ? (isAnnual ? Math.round(getDisplayPrice(25)) : '25') : (isAnnual ? Math.round(getDisplayPrice(35)) : '35')} per seat/month</div>
            </div>

            {/* Separator line */}
            <div 
              style={{
                width: '100%',
                height: '1px',
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                marginBottom: '16px',
              }}
            />
            
            {/* Total Credits */}
            <div className="mb-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-black dark:text-white">Total credits</span>
                <span className="text-sm font-medium text-black dark:text-white">
                  {((modalPlan === 'pro' ? 5000 : 25000) * teamSeats).toLocaleString()}/month
                </span>
              </div>
              <div className="text-xs text-[#999999] dark:text-[#666666]">Shared by all team members</div>
            </div>

            {/* Total Price */}
            <div className="mb-6">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-black dark:text-white">Total price</span>
                <span className="text-lg font-normal text-black dark:text-white" style={{ fontFamily: 'PP Editorial New, serif', fontStyle: 'italic' }}>
                  ${(() => {
                    const basePrice = modalPlan === 'pro' ? (isAnnual ? getDisplayPrice(99) : 99) : (isAnnual ? getDisplayPrice(349) : 349);
                    const additionalMembers = Math.max(0, teamSeats - 1);
                    const additionalPrice = modalPlan === 'pro' ? (isAnnual ? getDisplayPrice(25) : 25) : (isAnnual ? getDisplayPrice(35) : 35);
                    const monthlyTotal = basePrice + (additionalMembers * additionalPrice);
                    const total = isAnnual ? monthlyTotal * 12 : monthlyTotal;
                    return Math.round(total).toLocaleString();
                  })()}/{isAnnual ? 'year' : 'month'}
                </span>
              </div>
            </div>

            {/* Upgrade Button */}
            <button
              onClick={async () => {
                setSelectedPlan(modalPlan);
                setShowTeamModal(false);
                await handleContinue(modalPlan, teamSeats);
              }}
              disabled={isProcessing}
              className={`w-full py-3 rounded-full font-normal text-sm transition-all hover:opacity-80 ${
                isProcessing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{
                boxSizing: 'border-box',
                width: '100%',
                height: 'min-content',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor: darkMode ? '#ffffff' : '#000000',
                color: darkMode ? '#000000' : '#ffffff',
                overflow: 'visible',
                gap: '8px',
                borderRadius: '25px',
                border: 'none',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
              }}
            >
              {isProcessing ? 'Processing...' : `Continue with ${modalPlan === 'pro' ? 'Pro' : 'Max'}`}
            </button>

            {/* Features List */}
            <div className="mt-6" style={{ gap: '8px', display: 'flex', flexDirection: 'column' }}>
              <div className="flex items-center text-sm" style={{ opacity: 1 }}>
                <div 
                  className="flex items-center justify-center mr-2 flex-shrink-0"
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    border: darkMode ? '1px solid rgba(176, 135, 123, 0.5)' : '1px solid rgba(176, 135, 123, 0.4)',
                    backgroundColor: darkMode ? 'rgba(176, 135, 123, 0.2)' : 'rgba(176, 135, 123, 0.15)',
                  }}
                >
                  <svg className="w-3 h-3" style={{ color: '#B0877B' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm text-black dark:text-white">Shared team pool: {(modalPlan === 'pro' ? 5000 : 25000).toLocaleString()} credits per member per month</span>
              </div>
              <div className="flex items-center text-sm" style={{ opacity: 1 }}>
                <div 
                  className="flex items-center justify-center mr-2 flex-shrink-0"
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    border: darkMode ? '1px solid rgba(176, 135, 123, 0.5)' : '1px solid rgba(176, 135, 123, 0.4)',
                    backgroundColor: darkMode ? 'rgba(176, 135, 123, 0.2)' : 'rgba(176, 135, 123, 0.15)',
                  }}
                >
                  <svg className="w-3 h-3" style={{ color: '#B0877B' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm text-black dark:text-white">Unlimited access to Chat mode</span>
              </div>
              <div className="flex items-center text-sm" style={{ opacity: 1 }}>
                <div 
                  className="flex items-center justify-center mr-2 flex-shrink-0"
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    border: darkMode ? '1px solid rgba(176, 135, 123, 0.5)' : '1px solid rgba(176, 135, 123, 0.4)',
                    backgroundColor: darkMode ? 'rgba(176, 135, 123, 0.2)' : 'rgba(176, 135, 123, 0.15)',
                  }}
                >
                  <svg className="w-3 h-3" style={{ color: '#B0877B' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm text-black dark:text-white">Use advanced models in Agent mode</span>
              </div>
              {modalPlan === 'max' && (
                <>
                  <div className="flex items-center text-sm text-[#6B6B6B] dark:text-[#888888]">
                    <svg className="w-4 h-4 mr-2 text-[#B0877B]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Enterprise Features
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 text-center">
              <span className="text-sm text-[#999999] dark:text-[#666666]">
                Want to learn more? <a href="mailto:sales@heymidus.com" className="text-[#B0877B] hover:underline">Contact sales</a>
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer - hide when in onboarding flow */}
      {!isInOnboardingFlow && (
      <div className="absolute bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 transform -translate-x-1/2 text-[#898989] dark:text-[#585858] text-[13px] font-normal text-center whitespace-nowrap px-4 md:px-0 w-full md:w-auto pb-4">
        {/* Status indicator */}
        <div className="flex items-center justify-center mb-4">
          <a 
            href={getPublicStatusHomeUrl()} 
            className="flex items-center space-x-1 text-[11px] text-[#898989] dark:text-[#585858] hover:underline transition-opacity duration-200"
            onClick={(e) => {
              e.preventDefault();
              window.open(getPublicStatusHomeUrl(), '_blank');
            }}
          >
            {statusLoading ? (
              <>
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse"></div>
                <span>Checking status...</span>
              </>
            ) : systemStatus.isActive ? (
              <>
                <div className={`w-2 h-2 rounded-full ${
                  systemStatus.severity === 'critical' ? 'bg-red-500' :
                  systemStatus.severity === 'medium' ? 'bg-yellow-500' :
                  'dark:bg-[#585858] bg-[#898989]'
                }`}></div>
                <span>{systemStatus.title || 'System status update'}</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>All systems operational</span>
              </>
            )}
          </a>
        </div>
        <div className="flex flex-col md:flex-row items-center justify-center space-y-1 md:space-y-0 md:space-x-1 text-[11px] text-[#898989] dark:text-[#585858]">
          <div className="flex items-center">
            <span className="md:hidden">© {new Date().getFullYear()} Midus, a subsidiary of AgentCore LLC. All Rights Reserved.</span>
            <span className="hidden md:inline">© {new Date().getFullYear()} Midus, a subsidiary of AgentCore LLC. All Rights Reserved.</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="hidden md:inline md:mx-1">•</span>
            <a 
              href="https://www.heymidus.com/terms" 
              className="hover:opacity-100 transition-opacity duration-200 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
            <span className="px-0.5 md:px-1">•</span>
            <a 
              href="https://www.heymidus.com/privacy" 
              className="hover:opacity-100 transition-opacity duration-200 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
      )}
    </div>
    </>
  );
}

export default PaymentPage;
      